import React, { useState, useEffect } from 'react';
import { Link, useLocation, Outlet } from 'react-router-dom';
import '../styles/Layout.css';

import { useTheme } from '../context/ThemeContext';
import NotificationBell from './NotificationBell';

const Layout = () => {
    // Initialize based on screen width
    const [isSidebarOpen, setIsSidebarOpen] = useState(window.innerWidth > 768);
    const { isDarkMode, toggleTheme } = useTheme();
    const location = useLocation();

    // Close sidebar on mobile when route changes
    useEffect(() => {
        if (window.innerWidth <= 768) {
            setIsSidebarOpen(false);
        }
    }, [location.pathname]);

    const menuItems = [
        { path: '/dashboard', label: 'الرئيسية', icon: '🏠' },
        { path: '/courses', label: 'الدورات', icon: '📚' },
        { path: '/profile', label: 'الملف الشخصي', icon: '👤' },
        { path: '/support', label: 'الدعم والمساعدة', icon: '❓' },
    ];

    // Add Admin/Owner only items
    const userRole = localStorage.getItem("userRole");
    if (userRole === 'teacher') {
        menuItems.splice(4, 0, { path: '/ai-credits', label: 'رصيد الذكاء الاصطناعي', icon: '🤖' });
    }
    if (userRole === 'admin' || userRole === 'owner') {
        menuItems.splice(2, 0, { path: '/students', label: 'الطلاب', icon: '👨‍🎓' });
        menuItems.push(
            { path: '/admin/teachers', label: 'إدارة المعلمين', icon: '👔' },
            { path: '/admin/banners', label: 'إدارة الإعلانات', icon: '📢' },
            { path: '/admin/ai-credits', label: 'إدارة رصيد AI', icon: '💰' },
            { path: '/admin/activation-emails', label: 'رسائل التفعيل', icon: '✉️' },
            { path: '/legalpages', label: 'قانوني', icon: '⚖️' }
        );
    }

    const handleLogout = () => {
        localStorage.removeItem("authToken");
        localStorage.removeItem("userId");
        localStorage.removeItem("userRole");
        localStorage.removeItem("dbUserId");
        localStorage.removeItem("userStatus");
        // Force reload to clear state and avoid infinite redirection loops in App.js
        window.location.href = '/login';
    };

    return (
        <div className="layout-container">
            {/* Mobile Overlay */}
            <div 
                className={`sidebar-overlay ${isSidebarOpen ? 'active' : ''}`} 
                onClick={() => setIsSidebarOpen(false)}
            ></div>

            {/* Sidebar */}
            <aside className={`sidebar ${isSidebarOpen ? 'open' : 'closed'}`}>
                <div className="sidebar-header">
                    <Link to="/dashboard" className="logo-link">
                        <img src="/logo.webp" alt="DARESS" className="logo-image" />
                    </Link>
                    <button className="toggle-btn desktop-only" onClick={() => setIsSidebarOpen(!isSidebarOpen)} title="طي/توسيع القائمة">
                        {isSidebarOpen ? '◀' : '▶'}
                    </button>
                    <button className="toggle-btn mobile-only" onClick={() => setIsSidebarOpen(false)} title="إغلاق">
                        ✕
                    </button>
                </div>

                <nav className="sidebar-nav">
                    {menuItems.map((item) => (
                        <Link
                            key={item.path}
                            to={item.path}
                            className={`nav-item ${location.pathname === item.path ? 'active' : ''}`}
                            onClick={() => {
                                if (window.innerWidth <= 768) setIsSidebarOpen(false);
                            }}
                        >
                            <span className="icon">{item.icon}</span>
                            {isSidebarOpen && <span className="label">{item.label}</span>}
                        </Link>
                    ))}
                </nav>

                <div className="sidebar-footer">
                    <button onClick={toggleTheme} className="theme-toggle-btn" title={isDarkMode ? "الوضع النهاري" : "الوضع الليلي"}>
                        <span className="icon">{isDarkMode ? '☀️' : '🌙'}</span>
                        {isSidebarOpen && <span className="label">{isDarkMode ? 'الوضع النهاري' : 'الوضع الليلي'}</span>}
                    </button>
                    <button onClick={handleLogout} className="logout-btn">
                        <span className="icon">🚪</span>
                        {isSidebarOpen && <span className="label">خروج</span>}
                    </button>
                </div>
            </aside>

            {/* Main Content */}
            <main className="main-content">
                <header className="top-header">
                    <div className="header-left" style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                        <button
                            className="mobile-menu-btn"
                            onClick={() => setIsSidebarOpen(!isSidebarOpen)}
                        >
                            <span style={{ fontSize: '1.5rem' }}>☰</span>
                        </button>
                        <h2 className="page-title">
                            {menuItems.find(item => item.path === location.pathname)?.label || 'لوحة التحكم'}
                        </h2>
                    </div>
                    <div className="user-info" style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                        <NotificationBell />
                        <span className="user-badge">المعلم</span>
                    </div>
                </header>

                <div className="content-scrollable">
                    <Outlet />
                </div>
            </main>
        </div>
    );
};

export default Layout;
